import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Copy, Check, RefreshCw } from 'lucide-react';
import { Card, CardTitle, Button, Input } from '@/components/ui';
import { getShortUrlFromPlatform } from '@/utils/domainResolver';
import type { Platform } from '@/types';

interface QRCodeGeneratorProps {
  initialUrl?: string;
  initialCode?: string;
  platform?: Platform;
  checkNeeded?: boolean;
}

const QRCodeGenerator = ({ initialUrl, initialCode, platform = 'generic', checkNeeded = true }: QRCodeGeneratorProps) => {
  const { t } = useTranslation();
  // Extract code from URL if provided, handling both domains
  const extractCode = (urlStr?: string): string => {
    if (!urlStr) return '';
    return urlStr.replace(/^https?:\/\/[^/]+\//, '');
  };
  const [code, setCode] = useState(initialCode || extractCode(initialUrl));

  // Get the appropriate domain based on platform trust
  const shortUrl = getShortUrlFromPlatform(code, platform, checkNeeded);
  const url = `https://${shortUrl}`;
  const baseDomain = shortUrl.replace(`/${code}`, '');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [fgColor, setFgColor] = useState('#1A1A1A');
  const [size, setSize] = useState(256);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownloadPNG = useCallback(() => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size * 2;
    canvas.height = size * 2;

    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qrcode-${Date.now()}.png`;
      link.href = pngUrl;
      link.click();

      URL.revokeObjectURL(svgUrl);
    };

    img.src = svgUrl;
  }, [size, bgColor]);

  const handleDownloadSVG = useCallback(() => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const link = document.createElement('a');
    link.download = `qrcode-${Date.now()}.svg`;
    link.href = svgUrl;
    link.click();

    URL.revokeObjectURL(svgUrl);
  }, []);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const colorPresets = [
    { bg: '#FFFFFF', fg: '#1A1A1A', name: t('qr.presetClassic') },
    { bg: '#1A1A1A', fg: '#FFFFFF', name: t('qr.presetDark') },
    { bg: '#D4E157', fg: '#1A1A1A', name: t('qr.presetLime') },
    { bg: '#B8A9C9', fg: '#1A1A1A', name: t('qr.presetLavender') },
    { bg: '#F5F5F5', fg: '#1DB954', name: 'Spotify' },
    { bg: '#000000', fg: '#FF0000', name: 'YouTube' },
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Preview */}
      <Card variant="elevated" padding="lg">
        <CardTitle className="mb-6">{t('qr.preview')}</CardTitle>

        <div
          ref={qrRef}
          className="flex items-center justify-center p-8 rounded-2xl mb-6"
          style={{ backgroundColor: bgColor }}
        >
          <QRCodeSVG
            value={url}
            size={size}
            bgColor={bgColor}
            fgColor={fgColor}
            level="H"
            includeMargin={false}
          />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 flex items-center bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-lime-400/20 focus-within:border-lime-400">
            <span className="pl-4 pr-1 text-gray-500 dark:text-gray-400 text-sm whitespace-nowrap select-none">
              {baseDomain}/
            </span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="code"
              className="flex-1 py-3 pr-4 bg-transparent border-none outline-none text-dark-800 dark:text-white"
            />
          </div>
          <Button
            variant="ghost"
            onClick={handleCopyUrl}
          >
            {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={handleDownloadPNG}
          >
            {t('qr.downloadPng')}
          </Button>
          <Button
            variant="ghost"
            className="flex-1"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={handleDownloadSVG}
          >
            {t('qr.downloadSvg')}
          </Button>
        </div>
      </Card>

      {/* Customization */}
      <Card variant="default" padding="lg">
        <CardTitle className="mb-6">{t('qr.customize')}</CardTitle>

        {/* Color Presets */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-3">
            {t('qr.colorPresets')}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {colorPresets.map((preset) => (
              <button
                key={preset.name}
                onClick={() => {
                  setBgColor(preset.bg);
                  setFgColor(preset.fg);
                }}
                className={`p-3 rounded-xl border transition-all ${
                  bgColor === preset.bg && fgColor === preset.fg
                    ? 'border-lime-400 ring-2 ring-lime-200 dark:ring-lime-400/30'
                    : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500'
                }`}
              >
                <div
                  className="w-8 h-8 rounded-lg mx-auto mb-2"
                  style={{ backgroundColor: preset.bg, border: '1px solid #e5e5e5' }}
                >
                  <div
                    className="w-4 h-4 rounded m-2"
                    style={{ backgroundColor: preset.fg }}
                  />
                </div>
                <p className="text-xs text-dark-800 dark:text-gray-300">{preset.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
              {t('qr.backgroundColor')}
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="w-12 h-12 rounded-xl border border-gray-200 dark:border-dark-600 cursor-pointer"
              />
              <Input
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
              {t('qr.qrCodeColor')}
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={fgColor}
                onChange={(e) => setFgColor(e.target.value)}
                className="w-12 h-12 rounded-xl border border-gray-200 dark:border-dark-600 cursor-pointer"
              />
              <Input
                value={fgColor}
                onChange={(e) => setFgColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Size */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
            {t('qr.size')}: {size}px
          </label>
          <input
            type="range"
            min="128"
            max="512"
            step="32"
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="w-full accent-lime-500"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>128px</span>
            <span>512px</span>
          </div>
        </div>

        {/* Reset */}
        <Button
          variant="ghost"
          className="w-full"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={() => {
            setBgColor('#FFFFFF');
            setFgColor('#1A1A1A');
            setSize(256);
          }}
        >
          {t('qr.resetToDefault')}
        </Button>
      </Card>
    </div>
  );
};

export default QRCodeGenerator;
